import { camelize } from "humps";
import { getIn } from "formik";

/**
 * Translates a dot-separated sorting descriptor (e.g. "first_name.asc") into
 * the camelCased property key that consumers expect and whether the direction is ascending.
 *
 * @param sortingString The string returned from the UI component, formatted as "property.direction".
 * @returns The derived property key (camelCased) and a flag identifying the sort direction.
 */
export const parseSortingString = (sortingString: string): { sortProperty: string; isSortedAscending: boolean } => {
    const [sortProperty, sortDirection] = sortingString.split(".");
    return {
        sortProperty: camelize(sortProperty),
        isSortedAscending: sortDirection === "asc",
    };
};

/**
 * Sorts a list in-place by the provided key path while leaving null/undefined items at the end.
 * String values leverage localeCompare for predictable alphabetical ordering.
 *
 * @typeParam T The shape of the items being sorted.
 * @param array The list to sort; the array is mutated and also returned for chaining.
 * @param key A string or path array that identifies the property to sort by (compatible with Formik's getIn).
 * @param ascending When true sorts lowest to highest; when false sorts in descending order.
 * @returns The same array reference, sorted according to the provided key and direction.
 */
export const sortBy = <T>(array: T[], key: string | string[], ascending: boolean = true): T[] => {
    return array.sort((rowA, rowB) => {
        const valueA = getIn(rowA, key);
        const valueB = getIn(rowB, key);

        if ((typeof valueA === "undefined" || valueA === null) && (typeof valueB === "undefined" || valueB === null)) {
            return 0;
        }

        // Put any undefined or null values at the very bottom.
        if (typeof valueA === "undefined" || valueA === null) {
            return 1;
        }
        if (typeof valueB === "undefined" || valueB === null) {
            return -1;
        }

        const direction = ascending ? 1 : -1;
        if (typeof valueA === "string" && typeof valueB === "string" && valueA.localeCompare) {
            return direction * valueA.localeCompare(valueB);
        }

        if (valueA < valueB) {
            return direction * -1;
        } else if (valueA > valueB) {
            return direction * 1;
        } else {
            return 0;
        }
    });
};
